/**
 * apps/web :: lib/rulediff-browse-countries
 *
 * Catalog of home countries with full reviewer-approved rule-diff seed
 * coverage in `content/rule-diff/seed/`. Used ONLY by the rule-diff
 * browse page (`pages/RuleDiffBrowsePage.tsx`) — distinct from
 * `lib/countries.ts` which is the broader ISO 3166-1 catalog used by
 * the onboarding-wizard home-country picker.
 *
 * Lockstep with content/rule-diff/seed/{cc}-*.json. As the corpus
 * grows (Wave 17c added Chile; future waves may add more), append
 * here. The browse page filters its country dropdown by this list so
 * users don't pick a country that 404s on the seed lookup.
 *
 * 71 entries as of Wave 18g (2026-04-28).
 */

export interface BrowseCountry {
  /** Lowercase 2-letter code matching the seed filename prefix (e.g., "mx" from mx-ca.json). */
  code: string;
  /** English display name. */
  name: string;
  /**
   * `true` if the country drives on the LEFT — surfaces the dominant
   * side_of_road safety diff prominently when the destination is a RHS
   * jurisdiction (50 of 56 US jurisdictions, but NOT VI which is also
   * LHS — see Wave 17c VI special handling).
   */
  isLhs: boolean;
}

export const BROWSE_COUNTRIES: readonly BrowseCountry[] = [
  { code: "ar", name: "Argentina", isLhs: false },
  { code: "au", name: "Australia", isLhs: true },
  { code: "bd", name: "Bangladesh", isLhs: true },
  { code: "bg", name: "Bulgaria", isLhs: false },
  { code: "bo", name: "Bolivia", isLhs: false },
  { code: "br", name: "Brazil", isLhs: false },
  { code: "bs", name: "Bahamas", isLhs: true },
  { code: "bw", name: "Botswana", isLhs: true },
  { code: "cl", name: "Chile", isLhs: false },
  { code: "cn", name: "China", isLhs: false },
  { code: "co", name: "Colombia", isLhs: false },
  { code: "cr", name: "Costa Rica", isLhs: false },
  { code: "cu", name: "Cuba", isLhs: false },
  { code: "cz", name: "Czechia", isLhs: false },
  { code: "de", name: "Germany", isLhs: false },
  { code: "do", name: "Dominican Republic", isLhs: false },
  { code: "ec", name: "Ecuador", isLhs: false },
  { code: "eg", name: "Egypt", isLhs: false },
  { code: "es", name: "Spain", isLhs: false },
  { code: "et", name: "Ethiopia", isLhs: false },
  { code: "fr", name: "France", isLhs: false },
  { code: "gh", name: "Ghana", isLhs: false },
  { code: "gr", name: "Greece", isLhs: false },
  { code: "gt", name: "Guatemala", isLhs: false },
  { code: "gy", name: "Guyana", isLhs: true },
  { code: "hn", name: "Honduras", isLhs: false },
  { code: "ht", name: "Haiti", isLhs: false },
  { code: "hu", name: "Hungary", isLhs: false },
  { code: "id", name: "Indonesia", isLhs: true },
  { code: "il", name: "Israel", isLhs: false },
  { code: "in", name: "India", isLhs: true },
  { code: "iq", name: "Iraq", isLhs: false },
  { code: "ir", name: "Iran", isLhs: false },
  { code: "it", name: "Italy", isLhs: false },
  { code: "jm", name: "Jamaica", isLhs: true },
  { code: "jo", name: "Jordan", isLhs: false },
  { code: "jp", name: "Japan", isLhs: true },
  { code: "ke", name: "Kenya", isLhs: true },
  { code: "kr", name: "South Korea", isLhs: false },
  { code: "lb", name: "Lebanon", isLhs: false },
  { code: "lk", name: "Sri Lanka", isLhs: true },
  { code: "ma", name: "Morocco", isLhs: false },
  { code: "mx", name: "Mexico", isLhs: false },
  { code: "my", name: "Malaysia", isLhs: true },
  { code: "ng", name: "Nigeria", isLhs: false },
  { code: "ni", name: "Nicaragua", isLhs: false },
  { code: "np", name: "Nepal", isLhs: true },
  { code: "pa", name: "Panama", isLhs: false },
  { code: "pe", name: "Peru", isLhs: false },
  { code: "ph", name: "Philippines", isLhs: false },
  { code: "pk", name: "Pakistan", isLhs: true },
  { code: "pl", name: "Poland", isLhs: false },
  { code: "pt", name: "Portugal", isLhs: false },
  { code: "ro", name: "Romania", isLhs: false },
  { code: "ru", name: "Russia", isLhs: false },
  { code: "sk", name: "Slovakia", isLhs: false },
  { code: "so", name: "Somalia", isLhs: false },
  { code: "sv", name: "El Salvador", isLhs: false },
  { code: "sy", name: "Syria", isLhs: false },
  { code: "th", name: "Thailand", isLhs: true },
  { code: "tr", name: "Turkey", isLhs: false },
  { code: "tt", name: "Trinidad & Tobago", isLhs: true },
  { code: "tw", name: "Taiwan", isLhs: false },
  { code: "tz", name: "Tanzania", isLhs: true },
  { code: "ua", name: "Ukraine", isLhs: false },
  { code: "ug", name: "Uganda", isLhs: true },
  { code: "uk", name: "United Kingdom", isLhs: true },
  { code: "vn", name: "Vietnam", isLhs: false },
  { code: "za", name: "South Africa", isLhs: true },
  { code: "zm", name: "Zambia", isLhs: true },
  { code: "zw", name: "Zimbabwe", isLhs: true },
] as const;

/** Lookup helper. Returns `undefined` if the code isn't in the catalog. */
export function getBrowseCountry(code: string): BrowseCountry | undefined {
  const lc = code.toLowerCase();
  return BROWSE_COUNTRIES.find((c) => c.code === lc);
}

/**
 * Map an ISO 3166-1 alpha-2 uppercase code (used by `lib/countries.ts`
 * + the user profile's `home_country` field) to the lowercase browse
 * code (used by the rule-diff seed corpus filename convention).
 *
 * The two catalogs almost align (uppercase vs lowercase), but:
 *   - UK uses "GB" in ISO/profile and "uk" in the seed corpus, so we
 *     special-case it.
 */
export function isoToBrowseCode(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const lower = iso.toLowerCase();
  // GB ↔ uk is the only known divergence.
  const candidate = lower === "gb" ? "uk" : lower;
  return getBrowseCountry(candidate)?.code;
}
